import type { MarkAppSpaceViewedApiArg } from '@gm-commercial/rtk-clients/users/types';

/** Primary/trackable spaces from the profile mark-viewed API. */
export type TrackableAppSpaceCode = MarkAppSpaceViewedApiArg['space'];

/**
 * Every API trackable space must be present with a matching value.
 * Adding a new space on the API will fail typecheck until it is added here.
 */
const TRACKABLE_APP_SPACE_CODES = {
  BUSINESS_PROFILE: 'BUSINESS_PROFILE',
  VEHICLES: 'VEHICLES',
  DRIVERS: 'DRIVERS',
  USERS: 'USERS',
  GEOFENCES: 'GEOFENCES',
} as const satisfies { [K in TrackableAppSpaceCode]: K };

/** Additional getting-started resource cards (not marked via markAppSpaceViewed). */
const ADDITIONAL_APP_SPACE_CODES = {
  ORDERS: 'ORDERS',
  DATA_INSIGHTS: 'DATA_INSIGHTS',
  DRIVER_SCORES: 'DRIVER_SCORES',
  ONSTAR_PLANS: 'ONSTAR_PLANS',
  DEVICES: 'DEVICES',
} as const;

export const AppSpaceCode = {
  ...TRACKABLE_APP_SPACE_CODES,
  ...ADDITIONAL_APP_SPACE_CODES,
} as const;

export type AppSpaceCode = (typeof AppSpaceCode)[keyof typeof AppSpaceCode];

export const OrgScopedGettingStartedCards: readonly TrackableAppSpaceCode[] = [
  AppSpaceCode.BUSINESS_PROFILE,
  AppSpaceCode.VEHICLES,
  AppSpaceCode.DRIVERS,
  AppSpaceCode.USERS,
  AppSpaceCode.GEOFENCES,
];

export const FleetScopedGettingStartedCards: readonly TrackableAppSpaceCode[] =
  [AppSpaceCode.VEHICLES, AppSpaceCode.DRIVERS, AppSpaceCode.GEOFENCES];

export type AdditionalAppSpaceCode =
  (typeof ADDITIONAL_APP_SPACE_CODES)[keyof typeof ADDITIONAL_APP_SPACE_CODES];

export const OrgManagerAdditionalResources: readonly AdditionalAppSpaceCode[] =
  [
    AppSpaceCode.ORDERS,
    AppSpaceCode.DATA_INSIGHTS,
    AppSpaceCode.DRIVER_SCORES,
    AppSpaceCode.ONSTAR_PLANS,
  ];

export const FleetManagerAdditionalResources: readonly AdditionalAppSpaceCode[] =
  [
    AppSpaceCode.ORDERS,
    AppSpaceCode.DATA_INSIGHTS,
    AppSpaceCode.DRIVER_SCORES,
    AppSpaceCode.DEVICES,
  ];

export const APP_SPACE_CARD_SIZE: Record<
  AppSpaceCode,
  'small' | 'medium' | 'large'
> = {
  [AppSpaceCode.BUSINESS_PROFILE]: 'large',
  [AppSpaceCode.VEHICLES]: 'medium',
  [AppSpaceCode.DRIVERS]: 'medium',
  [AppSpaceCode.USERS]: 'medium',
  [AppSpaceCode.GEOFENCES]: 'medium',
  [AppSpaceCode.ORDERS]: 'small',
  [AppSpaceCode.DATA_INSIGHTS]: 'small',
  [AppSpaceCode.DRIVER_SCORES]: 'small',
  [AppSpaceCode.ONSTAR_PLANS]: 'small',
  [AppSpaceCode.DEVICES]: 'small',
};
